import { useState, useCallback } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';

import { apiCreateQuoteConsent } from 'api/quote-marketplace';

interface UseCoverageConsentConfirmationOptions<T = void> {
  quoteId: number;
  vehicleVin: string;
  vehicleId: number;
  consentType: number;
  onApplyChange: (newCode: string, clearOther: boolean) => void;
  shouldClearOther: (newCode: string) => boolean;
  getPendingData?: (newCode: string, currentCode: string) => T;
}

export interface CoverageConsentModalProps<T = void> {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (consentMethod: number) => void;
  isLoading: boolean;
  pendingData: T | null;
}

export const useCoverageConsentConfirmation = <T = void>({
  quoteId,
  vehicleVin,
  vehicleId,
  consentType,
  onApplyChange,
  shouldClearOther,
  getPendingData,
}: UseCoverageConsentConfirmationOptions<T>) => {
  const toast = useToast();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();

  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [pendingData, setPendingData] = useState<T | null>(null);
  const [isConsentLoading, setIsConsentLoading] = useState(false);

  const resetState = useCallback(() => {
    setPendingCode(null);
    setPendingData(null);
  }, []);

  const requestConfirmation = useCallback(
    (newCode: string, currentCode: string) => {
      setPendingCode(newCode);
      if (getPendingData) {
        setPendingData(getPendingData(newCode, currentCode));
      }
      onConfirmOpen();
    },
    [onConfirmOpen, getPendingData],
  );

  const applyChangeDirectly = useCallback(
    (newCode: string) => {
      const clearOther = shouldClearOther(newCode);
      onApplyChange(newCode, clearOther);
    },
    [onApplyChange, shouldClearOther],
  );

  const handleConfirm = useCallback(
    async (consentMethod: number) => {
      if (pendingCode === null) return;

      setIsConsentLoading(true);
      try {
        await apiCreateQuoteConsent(quoteId, {
          type: consentType,
          method: consentMethod,
          vehicle_vin: vehicleVin || undefined,
          vehicle_id: !vehicleVin ? vehicleId : undefined,
        });

        const clearOther = shouldClearOther(pendingCode);
        onApplyChange(pendingCode, clearOther);

        onConfirmClose();
        setPendingCode(null);
        setPendingData(null);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to record consent. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsConsentLoading(false);
      }
    },
    [
      pendingCode,
      quoteId,
      vehicleVin,
      vehicleId,
      consentType,
      shouldClearOther,
      onApplyChange,
      onConfirmClose,
      toast,
    ],
  );

  const handleCancel = useCallback(() => {
    setPendingCode(null);
    setPendingData(null);
    onConfirmClose();
  }, [onConfirmClose]);

  return {
    requestConfirmation,
    applyChangeDirectly,
    resetState,
    modalProps: {
      isOpen: isConfirmOpen,
      onClose: handleCancel,
      onConfirm: handleConfirm,
      isLoading: isConsentLoading,
      pendingData,
    },
  };
};
